import { Component, Input, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ENGINEERING_DEPARTMENT_OBSERVATION,
  HANDOVER_OBSERVATION_DATA_DIALOG_TITLE,
  HANDOVER_OBSERVATION_WELL_CONDITION,
  HANDOVER_OBSERVATION_WELL_CONDITION_DIALOG_TITLE,
  HANDOVER_TYPE_HO3_ID,
  OPERATIONS_DEPARTMENT_OBSERVATION,
  PERMISSION_ENGINEERING_COMPLETION,
} from 'src/app/handover-constants';
import { Handover } from 'src/app/interfaces/handover';
import {
  HandoverEmitterObservation,
  HandoverHistoryObservation,
  HandoverObservation,
  ObservationAttachment,
} from 'src/app/interfaces/handover-observation';
import { DialogService } from 'src/app/services/dialog.service';
import { HandoverService } from 'src/app/services/handover.service';
import { UserService } from 'src/app/services/user.service';
import { PendingChanges } from '../shared/pending-changes.class';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-handover-detail-footer-emitter',
  templateUrl: './handover-detail-footer-emitter.component.html',
  styleUrls: ['./handover-detail-footer-emitter.component.scss'],
  standalone: false,
})
export class HandoverDetailFooterEmitterComponent
  extends PendingChanges
  implements OnInit
{
  @Input() wellName: string | undefined;
  @Input() handover: Handover | undefined;
  @Input() readOnlyContent!: boolean;

  fullscreen = true;
  observations: HandoverEmitterObservation[] = [];
  hasNewObservations = false;
  receiverName = '';
  historyObservations: HandoverHistoryObservation[] = [];
  viewHistory: {
    handoverObservationTypeId: number;
    viewHistory: boolean;
  }[] = [];
  isEngineerUser = false;
  isHo3 = false;
  observationAttachment: any;
  dhoApiUrl = `${environment.urlBackend}/dhoapi`;

  constructor(
    private dialogService: DialogService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private handoverService: HandoverService,
    private userService: UserService
  ) {
    super();
    this.matIconRegistry.addSvgIcon(
      'check-fill-green',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/check-fill-green.svg'
      )
    );
    this.matIconRegistry.addSvgIcon(
      'close-fill-red',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/close-fill-red.svg'
      )
    );
    this.matIconRegistry.addSvgIcon(
      'check',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/check.svg'
      )
    );
    this.matIconRegistry.addSvgIcon(
      'close',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../../../../assets/close.svg'
      )
    );
  }

  ngOnInit(): void {
    this.isHo3 = this.handover?.handoverTypeId == HANDOVER_TYPE_HO3_ID;
    this.isEngineerUser = this.userService.hasPermission(
      PERMISSION_ENGINEERING_COMPLETION
    );
    this.getLastObservations();
    this.getObservations();
    this.getObservationAttachments();
  }

  getLastObservations(): void {
    let departmentObservation = '';
    if (this.isHo3) {
      departmentObservation = this.isEngineerUser
        ? ENGINEERING_DEPARTMENT_OBSERVATION
        : OPERATIONS_DEPARTMENT_OBSERVATION;
    }
    this.handoverService
      .getLastObservations(
        this.handover!.handoverId!,
        departmentObservation,
        false
      )
      .subscribe((emitterObservations: HandoverObservation[]) => {
        for (let index = 0; index < emitterObservations.length; index++) {
          const emitterObservation = emitterObservations[index];
          if (emitterObservation.handoverObservationId) {
            this.receiverName = emitterObservation.receiverName;
            this.observations.push({
              handoverObservationId: emitterObservation.handoverObservationId,
              handoverObservationTypeId:
                emitterObservation.handoverObservationTypeId,
              handoverObservationTypeDescription:
                emitterObservation.handoverObservationTypeDescription
                  .substring(0, 1)
                  .toUpperCase() +
                emitterObservation.handoverObservationTypeDescription.substring(
                  1,
                  emitterObservation.handoverObservationTypeDescription.length
                ),
              receiverObservation: emitterObservation.receiverObservation,
              emitterUserId: this.userService.getUserProperties().userId,
              emitterDescriptionObservationSolved: '',
              emitterDescriptionObservationNotSolved: '',
              emitterObservationSolved: false,
              emitterObservationNotSolved: false,
              emitterObservationDatetime:
                emitterObservation.emitterObservationDatetime,
              showEmitterObservation:
                !emitterObservation.emitterObservationSolved,
              departmentObservation: emitterObservation.departmentObservation,
            } as HandoverEmitterObservation);
          }
        }
        this.hasNewObservations =
          this.observations.filter(
            (observation) => observation.showEmitterObservation
          ).length > 0;
      });
  }

  isPendingChanges() {
    const hasPendingChanges = this.observations.some(
      (obs) => obs.emitterObservationNotSolved || obs.emitterObservationSolved
    );
    return hasPendingChanges;
  }

  observationNotSolved(index: number): void {
    this.observations[index].emitterObservationNotSolved =
      !this.observations[index].emitterObservationNotSolved;
    this.observations[index].emitterObservationSolved = false;
    this.emitFooterPendingChanges(this.isPendingChanges());
  }

  observationSolved(index: number): void {
    this.observations[index].emitterObservationSolved =
      !this.observations[index].emitterObservationSolved;
    this.observations[index].emitterObservationNotSolved = false;
    this.emitFooterPendingChanges(this.isPendingChanges());
  }

  resizeFooter(): void {
    this.fullscreen = !this.fullscreen;
  }

  getObservations(): void {
    this.handoverService
      .getObservations(this.handover!.handoverId!)
      .subscribe((historyObservations: HandoverHistoryObservation[]) => {
        this.historyObservations = historyObservations;
        this.historyObservations.forEach((o) => {
          o.handoverObservationTypeDescription =
            o.handoverObservationTypeDescription.substring(0, 1).toUpperCase() +
            o.handoverObservationTypeDescription.substring(
              1,
              o.handoverObservationTypeDescription.length
            );
        });
        // Group observations by type and count
        const countsHo = historyObservations.reduce(
          (result: any, handoverObservation: any) => {
            const prop = handoverObservation.handoverObservationTypeId;
            if (!result.hasOwnProperty(prop)) {
              result[prop] = 0;
            }
            result[prop]++;
            return result;
          },
          {}
        );
        this.viewHistory = Object.keys(countsHo).map((k) => {
          return {
            handoverObservationTypeId: Number(k),
            viewHistory: countsHo[k] > 1,
          };
        });
      });
  }

  viewHistoryHoType(
    handoverObservationTypeId: number,
    showEmitterObservation: boolean
  ): boolean {
    return (
      this.viewHistory.filter(
        (vh) =>
          vh.handoverObservationTypeId == handoverObservationTypeId &&
          (vh.viewHistory || !showEmitterObservation)
      ).length > 0
    );
  }

  viewObservationsHistory(handoverObservationTypeId: number): void {
    const isSendingUserLogged =
      this.userService.getUserProperties().userId ==
      this.handover?.sendingUserId;
    const isSendingEngineerUserLogged =
      this.userService.getUserProperties().userId ==
      this.handover?.sendingEngineerUserId;
    let emitterName = '';
    let engineerEmitterName = '';

    if (isSendingUserLogged) {
      emitterName =
        this.userService.getUserProperties().firstname +
        ' ' +
        this.userService.getUserProperties().lastname;
      engineerEmitterName = this.handover?.sendingEngineerUser || '';
    }
    if (isSendingEngineerUserLogged) {
      engineerEmitterName =
        this.userService.getUserProperties().firstname +
        ' ' +
        this.userService.getUserProperties().lastname;
      emitterName = this.handover?.sendingUser || '';
    }

    this.dialogService.openHandoverHistoryObservationsDialog(
      handoverObservationTypeId == HANDOVER_OBSERVATION_WELL_CONDITION
        ? HANDOVER_OBSERVATION_WELL_CONDITION_DIALOG_TITLE
        : HANDOVER_OBSERVATION_DATA_DIALOG_TITLE,
      this.historyObservations.filter(
        (ho) => ho.handoverObservationTypeId == handoverObservationTypeId
      ),
      emitterName,
      engineerEmitterName,
      this.handover!.handoverTypeId!
    );
  }

  getObservationAttachments(): void {
    if (this.handover?.handoverId) {
      this.handoverService
        .getObservationAttachments(this.handover?.handoverId)
        .subscribe((files: ObservationAttachment[]) => {
          if (files) {
            this.observationAttachment = files;
          }
        });
    }
  }
}
